import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';

import {
  getRouteParams,
  go,
} from '@/modules/core/client/services/angular-compat';
import * as messagesAPI from '@/modules/messages/client/api/messages.api';
import * as usersAPI from '@/modules/users/client/api/users.api';
import * as offersAPI from '@/modules/offers/client/api/offers.api';
import { userType } from '@/modules/users/client/users.prop-types';
import Monkeybox from '@/modules/users/client/components/Monkeybox';
import ReportMember from '@/modules/support/client/components/ReportMember.component';
import BlockMember from '@/modules/users/client/components/BlockMember.component';
import BlockedMemberBanner from '@/modules/users/client/components/BlockedMemberBanner.component';
import ThreadReply from '@/modules/messages/client/components/ThreadReply';
import Activate from '@/modules/users/client/components/Activate';
import ThreadMessages from '@/modules/messages/client/components/ThreadMessages';
import QuickReply from '@/modules/messages/client/components/QuickReply';
import Flashcard from '@/modules/messages/client/components/Flashcard';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';
import ReferenceThread from '@/modules/references-thread/client/components/ReferenceThread';
import plainTextLength from '@/modules/core/client/filters/plain-text-length.client.filter';
import { update as updateUnreadMessageCount } from '@/modules/messages/client/services/unread-message-count.client.service';
import { createHostingRequest } from '../api/hosting.api';

// Required by LanguageList in Monkeybox component
// @TODO: move this to higher up in the React tree once we no longer deal with Angular
const queryClient = new QueryClient();

const api = {
  messages: messagesAPI,
  users: usersAPI,
  offers: offersAPI,
};

const ThreadContainer = styled.div`
  position: fixed;
  top: 44px;
  bottom: 0;
  width: 100%;
  @media (min-width: 768px) {
    width: 505px;
    bottom: 12px;
  }
  @media (min-width: 992px) {
    width: 667px;
  }
  @media (min-width: 1200px) {
    width: 800px;
  }
  display: flex;
  flex-direction: column;
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  text-align: center;
`;

const FlexGrow = styled.div`
  flex-grow: 1;
`;

function YouHaveNotBeenTalkingYet() {
  const { t } = useTranslation('messages');
  return (
    <div className="content-empty">
      <i className="icon-3x icon-messages-alt" />
      <h4>{t("You haven't been talking yet.")}</h4>
      <Flashcard />
    </div>
  );
}

function YourProfileSeemsQuiteEmpty() {
  const { t } = useTranslation('messages');
  return (
    <div className="content-empty">
      <i className="icon-3x icon-messages-alt" />
      <p className="lead">
        {t('Your profile seems quite empty.')}
        <br />
        {t('Please write longer profile description before sending messages.')}
        <br />
        <a href="/profile/edit">{t('Edit your profile')}</a>
      </p>
    </div>
  );
}

function UserDoesNotExist() {
  const { t } = useTranslation('messages');
  return (
    <div className="content-empty">
      <i className="icon-3x icon-messages-alt" />
      <h4>{t("This user isn't a member anymore.")}</h4>
    </div>
  );
}

function Loading() {
  return (
    <LoadingContainer>
      <LoadingIndicator />
    </LoadingContainer>
  );
}

export default function Thread({ user, profileMinimumLength }) {
  const { t } = useTranslation('messages');

  if (!user.public) {
    return (
      <section className="container-spacer">
        <Activate />
      </section>
    );
  }

  const username = getRouteParams().username;

  if (user.username === username) {
    go('inbox');
    return null; // important to return null to indicate "nothing to render"
  }

  const [nextParams, setNextParams] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [doesNotExist, setDoesNotExist] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const initialBookingDetails = {
    type: 'daily',
    stays: 0,
    checkInDate: new Date(),
    checkOutDate: new Date(),
    notes: '',
    totalCost: 0,
  };
  const [bookingDetails, setBookingDetails] = useState(initialBookingDetails);
  const [hostPricing, setHostPricing] = useState([]);

  const cacheKey = `messages.thread.${user._id}-${username}`;
  const isBlocked = user.blocked?.includes(otherUser?._id);

  const hasEmptyProfile = useMemo(
    () => plainTextLength(user.description) < profileMinimumLength,
    [user],
  );

  const userHasReplied = Boolean(
    messages.find(message => message.userFrom._id === user._id),
  );

  const showReply = (messages.length > 0 || !hasEmptyProfile) && !removed;

  const showQuickReply = showReply && !userHasReplied;

  const isExtraSmall = useMediaQuery({ maxWidth: 768 - 1 });

  async function fetchMoreData() {
    if (isFetchingMore || !nextParams) return;
    setIsFetchingMore(true);
    try {
      const { messages: moreMessages, nextParams: moreNextParams } =
        await api.messages.fetchMessages(otherUser._id, nextParams);
      setMessages(messages => [
        ...moreMessages.sort((a, b) => a.created.localeCompare(b.created)),
        ...messages,
      ]);
      setNextParams(moreNextParams);
    } finally {
      setIsFetchingMore(false);
    }
  }

  function createFakeUserObject(userId) {
    return {
      _id: userId,
      displayName: t('Unknown member'),
      username: null,
      member: [],
      languages: [],
    };
  }

  async function fetchData() {
    if (isFetching) return;
    try {
      setIsFetching(true);
      let otherUser;
      let userRemoved = false;
      try {
        otherUser = await api.users.fetch(username);
      } catch (error) {
        if (error.response?.status === 404) {
          const userId = getRouteParams().userId;
          if (userId !== undefined) {
            otherUser = createFakeUserObject(userId);
            userRemoved = true;
          } else {
            setDoesNotExist(true);
            throw error;
          }
        } else {
          throw error;
        }
      }
      setRemoved(userRemoved);

      const { messages, nextParams } = await api.messages.fetchMessages(
        otherUser._id,
      );

      const offers = await api.offers.getOffers(otherUser._id, 'host');
      setHostPricing(offers[0]?.prices ?? []);

      setOtherUser(otherUser);

      const sortedMessages = messages.sort((a, b) =>
        a.created.localeCompare(b.created),
      );
      // TODO should be done at the back-end?
      if (userRemoved) {
        const filledMessages = messages.map(message => {
          if (!message.userTo) {
            message.userTo = { _id: otherUser._id };
          }
          if (!message.userFrom) {
            message.userFrom = { _id: otherUser._id };
          }
          return message;
        });
        setMessages(filledMessages);
      } else {
        setMessages(sortedMessages);
      }
      setNextParams(nextParams);
    } finally {
      setIsFetching(false);
    }
  }

  async function sendMessage(content) {
    const apiResponse = await api.messages
      .sendMessage(otherUser._id, content)
      .catch(error => {
        // Too many requests - error
        if (error?.response?.status === 429) {
          window.alert(
            t(
              'You are writing to too many people too fast. Slow down and try later again.',
            ),
          );
        } else {
          window.alert(
            t(
              'Failed to send the message. Perhaps your internet went down? Please try again.',
            ),
          );
        }
      });

    // Failed to send message
    if (!apiResponse?.data) {
      return false;
    }

    // Append in thread the formatted message we got as a response from API
    const { data: message } = apiResponse;
    setMessages(messages => [...messages, message]);
    focus();
    return true;
  }

  function focus() {
    document.querySelector('#message-reply-content')?.focus();
  }

  function onModalHide() {
    setIsModalVisible(false);
  }

  const bookingCalculation = useCallback(
    booking => {
      const { type, stays } = booking;

      // Find the price for the selected type
      const pricing = hostPricing.find(p => p.type === type);
      const rate = pricing ? pricing.price : 0;

      return parseFloat((rate * stays).toFixed(2));
    },
    [hostPricing],
  );

  const formatDate = date => date.toISOString().split('T')[0];

  async function submitBookingRequest() {
    const { type, stays, checkInDate, checkOutDate, totalCost } =
      bookingDetails;
    const periodLabels = { daily: 'Day', weekly: 'Week', monthly: 'Month' };

    if (!type) return alert('Please select a booking type.');
    if (!stays || stays < 1)
      return alert('Please enter a valid number of stays.');

    const today = new Date();
    const checkIn = new Date(checkInDate);

    if (checkIn < today.setHours(0, 0, 0, 0))
      return alert('Check-in date cannot be in the past.');
    if (checkOutDate <= checkIn)
      return alert('Check-out date must be after check-in date.');
    if (!totalCost || totalCost <= 0)
      return alert('Invalid total cost. Please check your booking details.');

    try {
      // ✅ Call API
      const response = await createHostingRequest({
        hostId: otherUser._id,
        checkIn: bookingDetails.checkInDate,
        checkOut: bookingDetails.checkOutDate,
        message: bookingDetails.notes,
        type: bookingDetails.type,
        price:
          hostPricing.find(p => p.type === bookingDetails.type)?.price || 0,
        totalCost: bookingDetails.totalCost,
        currency: 'EUR',
      });

      const hostingRequestId = response?.data?.hostingRequest?._id;

      // ✅ Send system message
      const messageContent = `
      <p><b><i>New Hosting Request</i></b></p>
      <p>
        <i>You have received a new hosting. Please review the details below and reply to confirm.</i>
      </p>
      <p>
        <b>Hosting Type:</b> ${
          bookingDetails.type.charAt(0).toUpperCase() +
          bookingDetails.type.slice(1)
        }<br />
        <b>Duration:</b> ${bookingDetails.stays} ${
        periodLabels[bookingDetails.type]
      }${bookingDetails.stays > 1 ? 's' : ''}<br />
        <b>Check-in Date:</b> ${bookingDetails.checkInDate.toLocaleDateString()}<br />
        <b>Check-out Date:</b> ${bookingDetails.checkOutDate.toLocaleDateString()}<br />
        <b>Notes:</b> ${bookingDetails.notes || '—'}<br />
        <b>Total Cost:</b> €${bookingDetails.totalCost}
      </p>
      <p style="display: none;"><b>Request ID:</b>${hostingRequestId}</p>
    `;

      await sendMessage(messageContent);

      setBookingDetails(initialBookingDetails);
      onModalHide();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          'Failed to send hosting request. Please try again.',
      );
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    async function markRead() {
      const unreadMessages = messages
        .filter(message => !message.read) // only unread
        .filter(message => message.userFrom._id !== user._id); // only other persons messages
      if (unreadMessages.length > 0) {
        await api.messages.markRead(unreadMessages.map(message => message._id));
        setMessages(messages =>
          messages.map(message => ({ ...message, read: true })),
        );
        updateUnreadMessageCount();
      }
    }
    markRead();
  }, [messages]);

  // Update totalCost whenever relevant fields change
  useEffect(() => {
    const totalCost = bookingCalculation(bookingDetails);
    setBookingDetails(prev => ({ ...prev, totalCost }));
  }, [
    bookingDetails.type,
    bookingDetails.stays,
    bookingDetails.checkInDate,
    bookingDetails.checkOutDate,
    bookingCalculation,
  ]);

  // Update check-out date whenever check-in, type, or stays change
  useEffect(() => {
    const { checkInDate, type, stays } = bookingDetails;
    const newCheckOutDate = new Date(checkInDate);

    if (type === 'daily') {
      newCheckOutDate.setDate(newCheckOutDate.getDate() + stays);
    } else if (type === 'weekly') {
      newCheckOutDate.setDate(newCheckOutDate.getDate() + stays * 7);
    } else if (type === 'monthly') {
      newCheckOutDate.setMonth(newCheckOutDate.getMonth() + stays);
    }

    setBookingDetails(prev => ({
      ...prev,
      checkOutDate: newCheckOutDate,
    }));
  }, [bookingDetails.checkInDate, bookingDetails.type, bookingDetails.stays]);

  if (doesNotExist) {
    return (
      <section className="container-spacer">
        <UserDoesNotExist />
      </section>
    );
  }

  return (
    <section className="container container-spacer">
      <div className="row">
        <div className="col-xs-12 col-sm-9">
          {isFetching && <Loading />}
          {!isFetching && (
            <ThreadContainer>
              {isFetchingMore && <Loading />}
              {messages.length === 0 ? (
                <>
                  <FlexGrow />
                  {hasEmptyProfile ? (
                    <YourProfileSeemsQuiteEmpty />
                  ) : (
                    <YouHaveNotBeenTalkingYet />
                  )}
                  <FlexGrow />
                </>
              ) : (
                <>
                  <ThreadMessages
                    user={user}
                    otherUser={otherUser}
                    messages={messages}
                    profileMinimumLength={profileMinimumLength}
                    onFetchMore={fetchMoreData}
                  />
                </>
              )}
              {!isBlocked && (showQuickReply || hostPricing.length > 0) && (
                <QuickReply
                  onSend={content => sendMessage(content)}
                  onFocus={focus}
                  isHost={hostPricing.length > 0}
                  openHostingRequestPopup={() => {
                    setIsModalVisible(true);
                  }}
                />
              )}
              {/* <button
                key="Hosting Request"
                className={`btn btn - sm btn - offer - hosting - no`}
                onClick={() => {
                  setIsModalVisible(true);
                }}
              >
                Hosting Request
              </button> */}
              {!isBlocked && showReply && (
                <ThreadReply
                  cacheKey={cacheKey}
                  onSend={content => sendMessage(content)}
                />
              )}
              {removed && (
                <div className="panel panel-default">
                  <div className="panel-body">
                    <em className="text-danger">
                      {t('Member is not available anymore.')}
                    </em>
                  </div>
                </div>
              )}
              {isBlocked && (
                <BlockedMemberBanner username={otherUser.username} />
              )}
            </ThreadContainer>
          )}
        </div>
        {otherUser && !isExtraSmall && !removed && (
          <div className="col-sm-3 text-center">
            <QueryClientProvider client={queryClient}>
              <Monkeybox user={otherUser} otherUser={user} />
            </QueryClientProvider>
            {messages.length > 0 && (
              <ReferenceThread userToId={otherUser._id} />
            )}
            <ReportMember
              className="btn btn-sm btn-default"
              username={otherUser.username}
            />
            <br />
            <br />
            <BlockMember
              className="btn btn-sm btn-default"
              username={otherUser.username}
              isBlocked={isBlocked}
            />
          </div>
        )}

        <Modal show={isModalVisible} onHide={onModalHide}>
          <Modal.Header closeButton>
            <Modal.Title>{t('Hosting Request')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Booking Type */}
            <div className="form-group mb-3">
              <label htmlFor="type" className="control-label">
                Hosting Type
              </label>
              <select
                id="type"
                className="form-control"
                value={bookingDetails.type}
                onChange={e =>
                  setBookingDetails({
                    ...bookingDetails,
                    type: e.target.value,
                  })
                }
              >
                <option value="">Select type</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Number of Stays */}
            <div className="form-group mb-3">
              <label htmlFor="stays" className="control-label">
                {bookingDetails.type
                  ? `${bookingDetails.type
                      .charAt(0)
                      .toUpperCase()}${bookingDetails.type.slice(1)} Stays`
                  : 'Number of Stays'}
              </label>
              <input
                type="number"
                id="stays"
                className="form-control"
                min="1"
                value={bookingDetails.stays}
                onChange={e =>
                  setBookingDetails({
                    ...bookingDetails,
                    stays: parseInt(e.target.value, 10) || 1,
                  })
                }
              />
            </div>

            {/* Check-in Date */}
            <div className="form-group mb-3">
              <label htmlFor="check-in-date" className="control-label">
                Check-in Date
              </label>
              <input
                type="date"
                id="check-in-date"
                className="form-control"
                value={bookingDetails.checkInDate.toISOString().split('T')[0]}
                min={new Date().toISOString().split('T')[0]}
                onChange={e =>
                  setBookingDetails({
                    ...bookingDetails,
                    checkInDate: new Date(e.target.value),
                  })
                }
              />
            </div>

            {/* Check-out Date */}
            <div className="form-group mb-3">
              <label htmlFor="check-out-date" className="control-label">
                Check-out Date
              </label>
              <input
                type="date"
                id="check-out-date"
                className="form-control"
                value={formatDate(bookingDetails.checkOutDate)}
                readOnly
              />
            </div>

            {/* Notes */}
            <div className="form-group mb-4">
              <label htmlFor="notes" className="control-label">
                Additional Notes
              </label>
              <textarea
                id="notes"
                className="form-control"
                placeholder="Add any special requests or details..."
                value={bookingDetails.notes || ''}
                onChange={e =>
                  setBookingDetails({
                    ...bookingDetails,
                    notes: e.target.value,
                  })
                }
              />
            </div>

            {/* Total Cost */}
            <div className="panel panel-success">
              <div className="panel-body row">
                <div className="col-xs-7">
                  <strong>
                    Total for {bookingDetails.stays} {bookingDetails.type} stay
                    {bookingDetails.stays > 1 ? 's' : ''}:
                  </strong>
                </div>
                <div className="col-xs-5 text-right">
                  <strong className="text-success h4">
                    €{bookingDetails.totalCost}
                  </strong>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-default pull-left" onClick={onModalHide}>
              {t('Cancel')}
            </button>
            <button onClick={submitBookingRequest} className="btn btn-primary">
              {t('Submit')}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </section>
  );
}

Thread.propTypes = {
  user: userType.isRequired,
  profileMinimumLength: PropTypes.number.isRequired,
};
